import type { WitnessSubject } from "@ondo/shared";

/**
 * The Record tab — OND-53. What the witness says about a work, in words that
 * claim exactly what the proof proves and nothing more.
 *
 * THE SENTENCES HERE ARE THE WHOLE OF WHAT MAY BE CLAIMED. `claims.test.ts`
 * holds the line from the other side: "cannot be changed", "immutable" and
 * "verified true" are false claims and the test refuses them in any constants
 * module. What is true is narrower and is said below: a line existed in this
 * form at a time, and has not changed since.
 */
export const WITNESS_COPY = {
  title: "Record",
  help:
    "Every line of this record was fingerprinted the moment it was written, folded into an " +
    "hourly root, and that root anchored to the Bitcoin blockchain through OpenTimestamps. " +
    "Nothing readable leaves ONDO. Each entry below says when a line was witnessed and which " +
    "block holds it.",
  limit:
    "A witness proves that a line existed in this form at the time shown and has not changed " +
    "since. It does not prove the line is true, and ONDO concludes nothing from it.",
  loading: "Reading the witness…",
  failed: "The witness could not be read just now. The record itself is unaffected.",
  empty: "Nothing has been witnessed yet.",
  download: "Download proof bundle",
  downloadNote:
    "A JSON file carrying every fingerprint, salt, path and receipt above, for checking " +
    "without ONDO: the open-source ots tool reads each receipt against a copy of the Bitcoin chain.",
  checkedLocally: "Recomputed in this browser",
  checkElsewhere: "Check a bundle outside your account, or read how to check it with no ONDO at all →",
  witnessedAt: "Witnessed",
  fieldOpened: "field opened",
  fieldsOpened: "fields opened",
  openHour: "Awaiting the close of the hour",
  awaitingBlock: "Awaiting a Bitcoin block",
  block: "Bitcoin block",
  shredded: "Erased at the subject's request; the fingerprint stays, the contents cannot be shown",
  utc: "UTC",
} as const;

/** What a leaf is about, as a reader would call it. */
export const WITNESS_SUBJECT_LABELS: Record<WitnessSubject, string> = {
  work: "Registration",
  provenance_entry: "History line",
  provenance_attestation: "Confirmation or dispute",
  provenance_document: "Document",
  transfer: "Transfer",
};

/**
 * A state as a verb. A state this build has never heard of is shown as it is
 * spelled, which is the tolerance `packages/shared` asks of every reader of an
 * enum: a newer migration may add one before the client has a word for it.
 */
export const WITNESS_STATE_LABELS: Record<string, string> = {
  registered: "Registered",
  written: "Written",
  attached: "Attached to the work",
  ended: "Ended",
  made: "Made",
  withdrawn: "Withdrawn",
  hashed: "Contents fingerprinted",
  offered: "Offered",
  accepted: "Accepted",
  declined: "Declined",
  unregistered: "Recorded for an off-ONDO buyer",
};

export const WITNESS_CHECK_LABELS = {
  verified: "Fingerprint, path and root recomputed and match",
  "awaiting-close": "Fingerprint recomputed and matches; no root to climb to yet",
  failed: "Does not recompute",
  shredded: "Nothing to recompute",
} as const;
